import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import cross_logo from '../Assets/Icons/cross.png';
import { AddNotification, EditNotification, FetchNotifications, FetchUsersInBusiness } from '../appActions';
import InfoButton from './InfoButton';
import './AddGraphForm.css'; // Reusing the same CSS for consistency

function AddNotificationForm({ setCloseModal, user_det, edit_obj }) {
  const { register, handleSubmit, getValues, setValue, watch } = useForm({
    defaultValues: {
      ...edit_obj,
      project_id: edit_obj?.device_id?.project_id,
      device_id: edit_obj?.device_id?._id,
      sensor_id: edit_obj?.sensor_id?._id,
    },
    shouldUnregister: false,
  });

  const dispatch = useDispatch();
  const state = useSelector((state) => state);

  const [step, setStep] = useState(1);
  const [projectOptions, setProjectOptions] = useState([]);
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [sensorOptions, setSensorOptions] = useState([]);
  const [selectedSensor, setSelectedSensor] = useState({});
  const [businessSearch, setBusinessSearch] = useState("");
  const [projectSearch, setProjectSearch] = useState("");
  const [deviceSearch, setDeviceSearch] = useState("");
  const [businessSearchOpen, setBusinessSearchOpen] = useState(false);
  const [projectSearchOpen, setProjectSearchOpen] = useState(false);
  const [deviceSearchOpen, setDeviceSearchOpen] = useState(false);

  const watchedValues = watch();

  // Local state for problematic fields
  const [upperLimit, setUpperLimit] = useState(edit_obj?.upper_limit || '');
  const [lowerLimit, setLowerLimit] = useState(edit_obj?.lower_limit || '');
  const [upperThreshold, setUpperThreshold] = useState(edit_obj?.upper_threshold || '');
  const [lowerThreshold, setLowerThreshold] = useState(edit_obj?.lower_threshold || '');
  const [notificationName, setNotificationName] = useState(edit_obj?.notification_name || '');

  const getObjectById = (arr, id) => {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i]._id === id) {
        return arr[i];
      }
    }
    return null;
  };

  useEffect(() => {
    if (!state.users_in_business) {
      dispatch(FetchUsersInBusiness({ business_id: user_det.business_id }));
    }


    if (edit_obj) {
      let biz = getObjectById(user_det.business_id, edit_obj.business_id);
      if (biz) setProjectOptions(biz.project_id);
      let proj = getObjectById(biz?.project_id, edit_obj.device_id?.project_id);
      if (proj) setDeviceOptions(proj.device_id);
      let dev = getObjectById(proj?.device_id, edit_obj.device_id?._id);
      if (dev) setSensorOptions(dev.sensor_id);
    }
  }, []);

  // If edit_obj changes (e.g., when clicking Edit in summary), update local state. 
  useEffect(() => {
    if (edit_obj) {
      setUpperLimit(edit_obj.upper_limit || '');
      setLowerLimit(edit_obj.lower_limit || '');
      setUpperThreshold(edit_obj.upper_threshold || '');
      setLowerThreshold(edit_obj.lower_threshold || '');
      setNotificationName(edit_obj.notification_name || '');
    }
  }, [edit_obj]);

  const handleNext = () => {
    if (step === 1 && watchedValues.business_id) {
      const business = getObjectById(user_det.business_id, watchedValues.business_id);
      if (business) setProjectOptions(business.project_id);
    }
    if (step === 2 && watchedValues.project_id) {
      const project = getObjectById(projectOptions, watchedValues.project_id);
      if (project) setDeviceOptions(project.device_id);
    }
    if (step === 3 && watchedValues.device_id) {
      const device = getObjectById(deviceOptions, watchedValues.device_id);
      if (device) setSensorOptions(device.sensor_id);
    }
    if (step === 4 && watchedValues.sensor_id) {
      const sensor = getObjectById(sensorOptions, watchedValues.sensor_id);
      if (sensor) setSelectedSensor(sensor);
    }
    // Limits validation in step 5. 
    if (step === 5) {
      const upper = parseFloat(upperLimit);
      const lower = parseFloat(lowerLimit);
      if (upper <= lower) {
        alert('Upper Limit must be greater than Lower Limit.');
        return;
      }
    }
    // Threshold validation in step 6.
    if (step === 6) {
      const upper = parseFloat(upperLimit);
      const lower = parseFloat(lowerLimit);
      const upperT = parseFloat(upperThreshold);
      const lowerT = parseFloat(lowerThreshold);
      if (upperT <= upper || lowerT >= lower) {
        alert('Upper and Lower Threshold values should be above and below the upper and lower limit values respectively.');
        return;
      }
    }
    setStep(step + 1);
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  const handleSelect = (field, value) => {
    const currentValue = getValues(field);
    if (currentValue === value) {
      setValue(field, null); // Deselect if clicked again
    } else {
      setValue(field, value);
    }

    // Reset subsequent fields on change
    const fieldsToReset = {
      'business_id': ['project_id', 'device_id', 'sensor_id'],
      'project_id': ['device_id', 'sensor_id'],
      'device_id': ['sensor_id'],
    };

    if (fieldsToReset[field]) {
      fieldsToReset[field].forEach(f => setValue(f, null));
    }
  };

  const onSubmit = (data) => {
    console.log("🔹 onSubmit triggered");
    console.log("📌 Initial data from form:", data);
    if (
      parseFloat(upperThreshold) <= parseFloat(upperLimit) ||
      parseFloat(lowerThreshold) >= parseFloat(lowerLimit)
    ) {
      alert('Upper and Lower Threshold values should be above and below the upper and lower limit values respectively.');
      return;
    }
    // Add local state values to data
    data.upper_limit = upperLimit;
    data.lower_limit = lowerLimit;
    data.upper_threshold = upperThreshold;
    data.lower_threshold = lowerThreshold;
    data.notification_name = notificationName;
    console.log("📌 Data after adding local state values:", data);
    if (edit_obj) {
      data._id = edit_obj._id;
      console.log("🔍 selectedSensor full object:", selectedSensor);
      data.topic = selectedSensor.sensor_type; 
      data.sensor_no = selectedSensor.sensor_no;
      data.sensor_name = selectedSensor.name;
      data.device_name = getObjectById(deviceOptions, getValues('device_id'))?.name;
      data.business_name = getObjectById(user_det.business_id, getValues('business_id'))?.name;
      console.log("✏️ Edit mode - final payload:", data);
      EditNotification(data).then((res) => {
        console.log("✅ EditNotification response:", res);
        if (res) {
          dispatch(FetchNotifications({ business_id: user_det.business_id }));
          setCloseModal(false);
        } else {
          alert('Error occurred, please try again!');
        }
      });
    } else {
      console.log("🔍 selectedSensor full object:", selectedSensor);
      data.topic = selectedSensor.sensor_type; 
      data.sensor_no = selectedSensor.sensor_no;
      data.sensor_name = selectedSensor.name;
      data.device_name = getObjectById(deviceOptions, getValues('device_id'))?.name;
      data.business_name = getObjectById(user_det.business_id, getValues('business_id'))?.name;
      console.log("➕ Add mode - final payload:", data);
      dispatch(
        AddNotification(data).then((res) => {
          console.log("✅ AddNotification response:", res);
          if (res) {
            dispatch(FetchNotifications({ business_id: user_det.business_id }));
            setCloseModal(false);
          } else {
            alert('Unable to add notification. Please try again!');
          }
        })
      );
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div>
            <h3 className="step-title">Select Business <InfoButton content={"Choose the business you want to add a notification for."} /></h3>
            <div className="search-row">
              {businessSearchOpen ? (
                <input
                  type="text"
                  className="search-input expanded"
                  placeholder="Search business..."
                  value={businessSearch}
                  autoFocus
                  onChange={e => setBusinessSearch(e.target.value)}
                  onBlur={e => {
                    if (!e.target.value) setBusinessSearchOpen(false);
                  }}
                  style={{marginBottom: 16, width: '100%', maxWidth: 400}}
                  onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                />
              ) : (
                <button
                  type="button"
                  className="search-icon-btn"
                  onClick={() => setBusinessSearchOpen(true)}
                  aria-label="Search"
                  style={{marginBottom: 16}}
                >
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="7" stroke="#7469B6" strokeWidth="2" />
                    <line x1="16" y1="16" x2="21" y2="21" stroke="#7469B6" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>
            <div className={`options-grid${user_det?.business_id?.filter(business => business.name.toLowerCase().includes(businessSearch.toLowerCase())).length === 1 ? ' single-option' : ''}`}>
              {user_det?.business_id
                ?.filter(business => business.name.toLowerCase().includes(businessSearch.toLowerCase()))
                .sort((a, b) => {
                  if (!businessSearch) return 0;
                  const aStarts = a.name.toLowerCase().startsWith(businessSearch.toLowerCase());
                  const bStarts = b.name.toLowerCase().startsWith(businessSearch.toLowerCase());
                  if (aStarts && !bStarts) return -1;
                  if (!aStarts && bStarts) return 1;
                  return 0;
                })
                .map((business) => (
                  <button
                    key={business._id}
                    type="button"
                    className={`option-button${watchedValues.business_id === business._id ? ' selected' : ''}`}
                    onClick={() => handleSelect('business_id', business._id)}
                  >
                    {business.name}
                  </button>
                ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div>
            <h3 className="step-title">Select Project <InfoButton content={"Pick a project under the selected business."} /></h3>
            <div className="search-row">
              {projectSearchOpen ? (
                <input
                  type="text"
                  className="search-input expanded"
                  placeholder="Search project..."
                  value={projectSearch}
                  autoFocus
                  onChange={e => setProjectSearch(e.target.value)}
                  onBlur={e => {
                    if (!e.target.value) setProjectSearchOpen(false);
                  }}
                  style={{marginBottom: 16, width: '100%', maxWidth: 400}}
                  onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                />
              ) : (
                <button
                  type="button"
                  className="search-icon-btn"
                  onClick={() => setProjectSearchOpen(true)}
                  aria-label="Search"
                  style={{marginBottom: 16}}
                >
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="7" stroke="#7469B6" strokeWidth="2" />
                    <line x1="16" y1="16" x2="21" y2="21" stroke="#7469B6" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>
            <div className={`options-grid${projectOptions?.filter(project => project.name.toLowerCase().includes(projectSearch.toLowerCase())).length === 1 ? ' single-option' : ''}`}>
              {projectOptions
                ?.filter(project => project.name.toLowerCase().includes(projectSearch.toLowerCase()))
                .sort((a, b) => {
                  if (!projectSearch) return 0;
                  const aStarts = a.name.toLowerCase().startsWith(projectSearch.toLowerCase());
                  const bStarts = b.name.toLowerCase().startsWith(projectSearch.toLowerCase());
                  if (aStarts && !bStarts) return -1;
                  if (!aStarts && bStarts) return 1;
                  return 0;
                })
                .map((project) => (
                  <button
                    key={project._id}
                    type="button"
                    className={`option-button${watchedValues.project_id === project._id ? ' selected' : ''}`}
                    onClick={() => handleSelect('project_id', project._id)}
                  >
                    {project.name}
                  </button>
                ))}
            </div>
          </div>
        );
      case 3:
        return (
          <div>
            <h3 className="step-title">Select Device <InfoButton content={"Select the device you want to monitor."} /></h3>
            <div className="search-row">
              {deviceSearchOpen ? (
                <input
                  type="text"
                  className="search-input expanded"
                  placeholder="Search device..."
                  value={deviceSearch}
                  autoFocus
                  onChange={e => setDeviceSearch(e.target.value)}
                  onBlur={e => {
                    if (!e.target.value) setDeviceSearchOpen(false);
                  }}
                  style={{marginBottom: 16, width: '100%', maxWidth: 400}}
                  onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                />
              ) : (
                <button
                  type="button"
                  className="search-icon-btn"
                  onClick={() => setDeviceSearchOpen(true)}
                  aria-label="Search"
                  style={{marginBottom: 16}}
                >
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="7" stroke="#7469B6" strokeWidth="2" />
                    <line x1="16" y1="16" x2="21" y2="21" stroke="#7469B6" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>
            <div className={`options-grid${deviceOptions?.filter(device => device.name.toLowerCase().includes(deviceSearch.toLowerCase())).length === 1 ? ' single-option' : ''}`}>
              {deviceOptions
                ?.filter(device => device.name.toLowerCase().includes(deviceSearch.toLowerCase()))
                .sort((a, b) => {
                  if (!deviceSearch) return 0;
                  const aStarts = a.name.toLowerCase().startsWith(deviceSearch.toLowerCase());
                  const bStarts = b.name.toLowerCase().startsWith(deviceSearch.toLowerCase());
                  if (aStarts && !bStarts) return -1;
                  if (!aStarts && bStarts) return 1;
                  return 0;
                })
                .map((device) => (
                  <button
                    key={device._id}
                    type="button"
                    className={`option-button${watchedValues.device_id === device._id ? ' selected' : ''}`}
                    onClick={() => handleSelect('device_id', device._id)}
                  >
                    {device.name}
                  </button>
                ))}
            </div>
          </div>
        );
      case 4:
        return (
          <div className="step-4">
            <h3 className="step-title">Select Sensor <InfoButton content={"Choose the sensor you want to monitor for notifications."} /></h3>
            <br></br><br />
            <div className='sensor-options-row single-option'>
              {sensorOptions?.map((sensor) => (
                <button
                  key={sensor._id}
                  type="button"
                  className={`option-button${watchedValues.sensor_id === sensor._id ? ' selected' : ''}`}
                  onClick={() => handleSelect('sensor_id', sensor._id)}
                >
                  {sensor.name}
                </button>
              ))}
            </div>
          </div>
        );
      case 5:
        return (
          <div className="form-inputs">
            <h3 className="step-title">Set Limits <InfoButton content={"Set the normal operating range for the sensor."} /></h3>
            <label className='form-label' htmlFor="upper_limit">
              <div>
                Upper Limit
                <InfoButton content={'Set a value within the sensor maximum range'} />
              </div>
             
              <input 
                id="upper_limit"
                type="text" 
                inputMode="decimal"
                className="full-width-input" 
                step="0.01"
                value={upperLimit}
                onChange={e => setUpperLimit(e.target.value)}
                placeholder="Enter upper limit"
              />
            </label>
            <label className='form-label' htmlFor="lower_limit">
              <div> Lower Limit
                <InfoButton content={'Set a value within the sensor minimum range.'} />
              </div>
             
              <input 
                id="lower_limit"
                type="text" 
                inputMode="decimal"
                className="full-width-input" 
                step="0.01"
                value={lowerLimit}
                onChange={e => setLowerLimit(e.target.value)}
                placeholder="Enter lower limit"
              />
            </label>
          </div>
        );
      case 6:
        return (
          <div className="form-inputs">
            <h3 className="step-title">Set Thresholds <InfoButton content={"set threshold value for sensor."} /></h3>
            <label className='form-label' htmlFor="upper_threshold">
              <div> Upper Threshold<InfoButton content={`Upper threshold must be greater than ${upperLimit || 'N/A'}`} /></div>
             
              <input 
                id="upper_threshold"
                type="text" 
                inputMode="decimal"
                className="full-width-input" 
                step="0.01"
                value={upperThreshold}
                onChange={e => setUpperThreshold(e.target.value)}
                placeholder="Enter upper threshold"
              />
            </label>
            <label className='form-label' htmlFor="lower_threshold">
              <div>Lower Threshold<InfoButton content={`Lower threshold must be less than ${lowerLimit || 'N/A'}`} /></div>
              
              <input 
                id="lower_threshold"
                type="text" 
                inputMode="decimal"
                className="full-width-input" 
                step="0.01"
                value={lowerThreshold}
                onChange={e => setLowerThreshold(e.target.value)}
                placeholder="Enter lower threshold"
              />
            </label>
          </div>
        );
      case 7:
        return (
          <div className="form-inputs">
            <h3 className="step-title">Notification Recipients <InfoButton content={"Select users who will receive email notifications "} /></h3>
            <label className='form-label' htmlFor="notification_name">
              Notification Name
             
              <input 
                id="notification_name"
                type="text" 
                className="full-width-input" 
                value={notificationName}
                onChange={e => setNotificationName(e.target.value)}
                placeholder="Enter notification name"
              />
            </label>
            <label className='form-label' htmlFor="">
            <br /> 
              Email Users 
              <div className="email-users-grid">
                {state?.users_in_business?.map((user) => (
                  <button
                    key={user._id}
                    type="button"
                    className={`option-button${getValues('email_users')?.includes(user.contact_email) ? ' selected' : ''}`}
                    onClick={() => {
                      const currentEmails = getValues('email_users') || [];
                      const email = user.contact_email;
                      if (currentEmails.includes(email)) {
                        setValue('email_users', currentEmails.filter(e => e !== email));
                      } else {
                        setValue('email_users', [...currentEmails, email], {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                    }}
                  >
                    {user.username} ({user.contact_email})
                  </button>
                ))}
              </div>
            </label>
              {/* SMS Users */}
            <label className='form-label' htmlFor="">
              <br /> 
              SMS Users 
              <div className="sms-users-grid">
                {state?.users_in_business?.map((user) => {
                  // ✅ logs inside block body
                  console.log("User object:", user);
                  console.log("SMS candidate number:", user.phone_number);

                  return (
                    <button
                      key={user._id}
                      type="button"
                      className={`option-button${
                        getValues('sms_users')?.includes(user.phone_number) ? ' selected' : ''
                      }`}
                      onClick={() => {
                        const currentNumbers = getValues('sms_users') || [];
                        const number = user.phone_number;
                        console.log("Clicked SMS number:", number); // 👀 what’s being used
                        let updated;

                        if (currentNumbers.includes(number)) {
                          updated = currentNumbers.filter(n => n !== number);
                        } else {
                          updated = [...currentNumbers, number];
                        }

                        setValue('sms_users', updated, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });

                        console.log('Updated SMS users:', updated);
                        console.log('All form values:', getValues());
                      }}
                    >
                      {user.username} ({user.phone_number})
                    </button>
                  );
                })}
              </div>
            </label>
          </div>
        );
      case 8:
        const businessName = getObjectById(user_det?.business_id, watchedValues.business_id)?.name;
        const projectName = getObjectById(projectOptions, watchedValues.project_id)?.name;
        const deviceName = getObjectById(deviceOptions, watchedValues.device_id)?.name;
        const sensorName = getObjectById(sensorOptions, watchedValues.sensor_id)?.name;
        
        return (
          <div className="summary-view">
            <h3 className="step-title">Summary <InfoButton content={"Review your selections. Use Edit to go back and change any step before submitting."} /></h3>
            <div className="summary-table">
              <div className="summary-row">
                <div className="summary-label">Business:</div>
                <div className="summary-value">{businessName || 'Not selected'}</div>
                <button type="button" className="summary-edit" onClick={() => setStep(1)}>Edit</button>
              </div>
              <div className="summary-row">
                <div className="summary-label">Project:</div>
                <div className="summary-value">{projectName || 'Not selected'}</div>
                <button type="button" className="summary-edit" onClick={() => setStep(2)}>Edit</button>
              </div>
              <div className="summary-row">
                <div className="summary-label">Device:</div>
                <div className="summary-value">{deviceName || 'Not selected'}</div>
                <button type="button" className="summary-edit" onClick={() => setStep(3)}>Edit</button>
              </div>
              <div className="summary-row">
                <div className="summary-label">Sensor:</div>
                <div className="summary-value">{sensorName || 'Not selected'}</div>
                <button type="button" className="summary-edit" onClick={() => setStep(4)}>Edit</button>
              </div>
              <div className="summary-row">
                <div className="summary-label">Upper Limit:</div>
                <div className="summary-value">{upperLimit || 'Not set'}</div>
                <button type="button" className="summary-edit" onClick={() => setStep(5)}>Edit</button>
              </div>
              <div className="summary-row">
                <div className="summary-label">Lower Limit:</div>
                <div className="summary-value">{lowerLimit || 'Not set'}</div>
                <button type="button" className="summary-edit" onClick={() => setStep(5)}>Edit</button>
              </div>
              <div className="summary-row">
                <div className="summary-label">Upper Threshold:</div>
                <div className="summary-value">{upperThreshold || 'Not set'}</div>
                <button type="button" className="summary-edit" onClick={() => setStep(6)}>Edit</button>
              </div>
              <div className="summary-row">
                <div className="summary-label">Lower Threshold:</div>
                <div className="summary-value">{lowerThreshold || 'Not set'}</div>
                <button type="button" className="summary-edit" onClick={() => setStep(6)}>Edit</button>
              </div>
              <div className="summary-row">
                <div className="summary-label">Notification Name:</div>
                <div className="summary-value">{notificationName || 'Not set'}</div>
                <button type="button" className="summary-edit" onClick={() => setStep(7)}>Edit</button>
              </div>
              <div className="summary-row">
                <div className="summary-label">Email Recipients:</div>
                <div className="summary-value">
                  {watchedValues.email_users?.length > 0 
                    ? watchedValues.email_users.join(', ') 
                    : 'None selected'}
                </div>
                <button type="button" className="summary-edit" onClick={() => setStep(7)}>Edit</button>
              </div>
              <div className="summary-row">
                <div className="summary-label">SMS Recipients:</div>
                <div className="summary-value">
                  {watchedValues.sms_users?.length > 0 
                    ? watchedValues.sms_users.join(', ') 
                    : 'None selected'}
                </div>
                <button type="button" className="summary-edit" onClick={() => setStep(7)}>Edit</button>
              </div>

            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const isNextDisabled = () => {
    switch (step) {
      case 1: return !watchedValues.business_id;
      case 2: return !watchedValues.project_id;
      case 3: return !watchedValues.device_id;
      case 4: return !watchedValues.sensor_id;
      case 5: {
        const hasAllFields = upperLimit && lowerLimit;
        return !hasAllFields;
      }
      case 6: {
        const hasAllFields = upperThreshold && lowerThreshold;
        return !hasAllFields;
      }
      case 7: {
        const hasAllFields = 
          notificationName && 
          (
            (watchedValues.email_users?.length > 0) ||
            (watchedValues.sms_users?.length > 0)
          ); // allow at least one email OR one SMS
        return !hasAllFields;
      }      
      default: return false;
    }
  };

  // Debug: log watchedValues on every change
  useEffect(() => {
    console.log('watchedValues', watchedValues);
  }, [watchedValues]);

  return (
    <div className="modal">
      <form onSubmit={handleSubmit(onSubmit)} className="multistep-form" autoComplete="off">
        <img
          onClick={() => setCloseModal(false)}
          className="form-cross-but"
          src={cross_logo}
          alt="close"
        />
        <div className="form-content">
          {renderStepContent()}
        </div>
        <div className="navigation-buttons">
          {step > 1 && (
            <a
              className="form-but-1 prev nav-link"
              onClick={e => { e.preventDefault(); handlePrevious(); }}
              href="#"
              tabIndex={0}
            >
             <span className="arrow">&#8592;</span> Previous 
            </a>
          )}
          {step < 8 && (
            <a
              className={`form-but-1 next nav-link${isNextDisabled() ? ' disabled' : ''}`}
              onClick={e => { 
                e.preventDefault(); 
                if (!isNextDisabled()) {
                  handleNext(); 
                } else {
                  // Show specific error messages for different steps
                  if (step === 5) {
                    alert('Please fill in all required fields.');
                  } else if (step === 6) {
                    alert('Please set both upper and lower thresholds.');
                  } else if (step === 7) {
                    alert('Please set a notification name and select at least one recipient (Email or SMS).');                  
                  } else {
                    alert('Please complete the current step before proceeding.');
                  }
                }
              }}
              href="#"
              tabIndex={0}
              aria-disabled={isNextDisabled()}
            >
              Next <span className="arrow">&#8594;</span>
            </a>
          )}
          {step === 8 && <button type="submit" className="form-but-1 submit">{edit_obj ? 'Update' : 'Add'} </button>}
        </div>
      </form>
    </div>
  );
}

export default AddNotificationForm;
